import { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { getAccessToken } from "./useAuth";
import { fetchWithAuth } from "../utils/fetchWithAuth";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const POLL_MS = 15 * 60 * 1000; // 15 min polling fallback

export function useTrending() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiEnhanced, setAiEnhanced] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const socketRef = useRef(null);
  const pollRef = useRef(null);

  async function fetchTrending() {
    try {
      const res = await fetchWithAuth(`${API}/api/trending`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data.items || []);
      setAiEnhanced((data.items || []).some(i => i.aiEnhanced));
      setUpdatedAt(data.updatedAt);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTrending();

    // Socket.IO for live trending updates
    const token = getAccessToken();
    const socket = io(API, {
      auth: token ? { token } : {},
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
    });
    socketRef.current = socket;

    socket.on("trendingUpdate", data => {
      setItems(data.items || []);
      setAiEnhanced((data.items || []).some(i => i.aiEnhanced));
      setUpdatedAt(data.updatedAt);
    });

    // Fallback polling
    pollRef.current = setInterval(fetchTrending, POLL_MS);

    return () => {
      socket.disconnect();
      clearInterval(pollRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { items, loading, error, aiEnhanced, updatedAt, refresh: fetchTrending };
}
